import React, { useEffect, useState } from "react";
import {
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
} from "@mui/material";
import errorIcon from "../../assets/icons/error-icon.svg";
import { Controller, useFormContext } from "react-hook-form";
import { useFolderDropdown } from "../../services/destination/index";
import {useCountryList} from "../../services/okta";
import { baseURL } from "../../shared/env";

const DestinationPickList = () => {
    const { control, setValue, trigger } = useFormContext();

    const { data: countries } = useCountryList();
    const availableCountries = Array.isArray(countries)
        ? countries.map((c: any) => ({
            name: c.name || c.code || '',
            tags: Array.isArray(c.tags) ? c.tags : []
        }))
        : [];

    const { data: rawData,  isLoading, error } = useFolderDropdown({
        query: {
            country: availableCountries.map(c => ({
                name: c.name,
                tags: c.tags
            }))
        }
    });

    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedBrand, setSelectedBrand] = useState("");
    const [selectedDestination, setSelectedDestination] = useState("");
    const [isAgency, setIsAgency] = useState("0"); // default: Reckitt (0)
    const [versionName, setVersionName] = useState("");
    const [selectedVersionName, setSelectedVersionName] = useState("");

    type VersionOption = {
        name: string;
        secret_id: string;
        account_id: string;
        [key: string]: any;
    };

    const [versionOptions, setVersionOptions] = useState<VersionOption[]>([]);
    const [selectedVersionId, setSelectedVersionId] = useState("");
    const parsed = rawData?.data ? JSON.parse(rawData.data) : [];
    const folders = Array.isArray(parsed) ? parsed : [];
    const country = folders.find((c: any) => c.folder1 === selectedCountry);
    const brand = country?.children.find((b: any) => b.name === selectedBrand);
    const destination = brand?.children.find((d: any) => d.name === selectedDestination);

    // default values
    useEffect(() => {
        if (!selectedCountry && folders.length > 0) {
            const firstCountry = folders[0].folder1;
            setSelectedCountry(firstCountry);
            setValue("country", firstCountry);
        }
    }, [folders, selectedCountry, setValue]);

    useEffect(() => {
        if (selectedCountry && country?.children.length > 0 && !selectedBrand) {
            const firstBrand = country.children[0].name;
            setSelectedBrand(firstBrand);
            setValue("brand", firstBrand);
            setVersionOptions([]);
            setSelectedVersionId("");
            setSelectedVersionName("");
            setValue("selected_version_name", "");
            setValue("secret_id", "");
        }
    }, [selectedCountry, country, selectedBrand, setValue]);

    useEffect(() => {
        if (selectedBrand && brand?.children.length > 0 && !selectedDestination) {
            const firstDest = brand.children[0].name;
            setSelectedDestination(firstDest);
            setValue("destination", firstDest);

            setVersionOptions([]);
            setSelectedVersionId("");
            setSelectedVersionName("");
            setValue("selected_version_name", "");
            setValue("secret_id", "");
        }
    }, [selectedBrand, brand, selectedDestination, setValue]);

    useEffect(() => {
        if (selectedDestination && destination?.children.length > 0 && versionOptions.length === 0) {
            setVersionOptions(destination.children);

            const firstVersion = destination.children[0];
            if (firstVersion) {
                setSelectedVersionId(firstVersion.secret_id);
                setSelectedVersionName(firstVersion.name);
                setValue("selected_version_name", firstVersion.name);
                setValue("secret_id", firstVersion.secret_id);
            }
        }
    }, [selectedDestination, destination, versionOptions.length, setValue]);



    useEffect(() => {
        setValue("isAgency", Number(isAgency));
    }, [isAgency, setValue]);

    if (isLoading) return <CircularProgress size={24} />;
    if (error) return <div>Error loading data</div>;
    if (folders.length === 0) return <div>No available folders</div>;

    return (
        <div className="flex-col">
            {/* Country */}
            <FormControl fullWidth>
                <div className="create-destination__dropdown-wrap__label">Country:</div>
                <Controller
                    name="country"
                    control={control}
                    render={() => (
                        <Select
                            value={selectedCountry}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedCountry(val);
                                setSelectedBrand("");
                                setSelectedDestination("");
                                setSelectedVersionId("");
                                setSelectedVersionName("");
                                setValue("country", val);
                                setValue("brand", "");
                                setValue("destination", "");
                                setValue("selected_version_name", "");
                                setValue("secret_id", "");
                                setVersionOptions([]);
                            }}
                        >
                            {folders.map((c: any) => (
                                <MenuItem className="dropdown-item" key={c.folder1} value={c.folder1}>
                                    {c.folder1}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            {/* Brand */}
            <div className="create-destination__divider-sml"></div>
            <FormControl fullWidth disabled={!selectedCountry}>
                <div className="create-destination__dropdown-wrap__label">Brand:</div>
                <Controller
                    name="brand"
                    control={control}
                    render={() => (
                        <Select
                            value={selectedBrand}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedBrand(val);
                                setSelectedDestination("");
                                setSelectedVersionId("");
                                setSelectedVersionName("");
                                setValue("brand", val);
                                setValue("destination", "");
                                setValue("selected_version_name", "");
                                setValue("secret_id", "");
                                setVersionOptions([]);
                            }}
                        >
                            {country?.children.map((b: any) => (
                                <MenuItem key={b.name} value={b.name}>
                                    {b.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>

            {/* Destination */}
            <div className="create-destination__divider-sml"></div>
            <FormControl fullWidth disabled={!selectedBrand}>
                <div className="create-destination__dropdown-wrap__label">Destination:</div>
                <Controller
                    name="destination"
                    control={control}
                    render={() => (
                        <Select
                            value={selectedDestination}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedDestination(val);
                                setSelectedVersionId("");
                                setSelectedVersionName("");
                                setValue("destination", val);
                                setValue("selected_version_name", "");
                                setValue("secret_id", "");
                                setVersionOptions([]);
                            }}
                        >
                            {brand?.children.map((d: any) => (
                                <MenuItem key={d.name} value={d.name}>
                                    {d.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>

            {/* Selected Version Name */}
            <div className="create-destination__divider-sml" />
            <FormControl fullWidth disabled={!selectedDestination || versionOptions.length === 1}>
                <div className="create-destination__dropdown-wrap__label">Choose version of destination to be reauthorized:</div>
                <Controller
                    name="selected_version_name"
                    control={control}
                    render={() => (
                        <Select
                            value={selectedVersionId}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedVersionId(val);
                                const selected = versionOptions.find((v: any) => v.secret_id === val);
                                if (selected) {
                                    setSelectedVersionName(selected.name);
                                    setValue("selected_version_name", selected.name);
                                    setValue("secret_id", selected.secret_id);
                                }
                            }}
                        >
                            {versionOptions.map((v: any) => {
                                const label = v.name === "" ? `Free slot – new destination will be authorized for Account ID: ${v.account_id}` : `${v.name} (Account ID: ${v.account_id})`;
                                return (
                                    <MenuItem key={v.secret_id} value={v.secret_id}>
                                        {label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    )}
                />
            </FormControl>

            {/* Version name */}
            <div className="create-destination__divider-sml"></div>
            <FormControl fullWidth>
                <div className="create-destination__dropdown-wrap__label">Set your destination name:</div>
                <Controller
                    name="version_name"
                    control={control}
                    rules={{
                        required: "Destination name is required",
                        validate: value => value.length > 0 || "Destination name is required",
                        maxLength: { value: 256, message: "Max length is 256 characters" },
                    }}
                    render={({ fieldState: { error } }) => {

                        return (
                            <>
                            <TextField fullWidth
                                value={versionName}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setVersionName(val);
                                    setValue("version_name", val);
                                    trigger("version_name");
                                }}
                            >
                            </TextField >
                            {error?.message && (
                                <div className="create-segment__input-wrap__segment-err">
                                <img src={errorIcon} alt="Error Icon" />
                                {error?.message}
                                </div>
                            )}
                        </>
                    )}

                    }
                />
            </FormControl>
            {/* Account type (isAgency) */}
            <div className="create-destination__divider-sml"></div>
            <FormControl component="fieldset" className="mb-4">
                <div className="create-destination__dropdown-wrap__label">Select account type:</div>
                <Controller
                    name="isAgency"
                    control={control}
                    render={() => (
                        <RadioGroup
                            value={isAgency}
                            onChange={(e) => {
                                const val = e.target.value;
                                setIsAgency(val);
                                setValue("isAgency", Number(val));
                            }}
                        >
                            <FormControlLabel value="1" control={<Radio />} label="Agency account" />
                            <FormControlLabel value="0" control={<Radio />} label="Reckitt account" />
                        </RadioGroup>
                    )}
                />
            </FormControl>
        </div>
    );
};

export default DestinationPickList;